import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, Sparkles, MapPin, Calendar, Users, DollarSign, X, Send } from "lucide-react";
import { api } from "../lib/api";

/**
 * NewBriefRequests — Phase 46 provider-dashboard widget.
 *
 * Lists open TREL Request Briefs eligible for the signed-in provider
 * (matched by category + city OR remote-capable). Provider can quickly
 * "Send Offer" inline (small modal) or Pass (hide locally).
 *
 * Eligibility is computed server-side via GET /api/briefs. Provider can
 * only have one offer per brief (re-opening the modal reuses the existing
 * offer's id and updates).
 */
export default function NewBriefRequests() {
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(() => new Set());
  const [openModal, setOpenModal] = useState(null);

  const reload = () => {
    setLoading(true);
    api.get("/briefs")
      .then((r) => setBriefs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setBriefs([]))
      .finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const visible = briefs.filter((b) => !hidden.has(b.brief_id));

  if (loading) return null;
  if (visible.length === 0) {
    return (
      <section
        data-testid="new-brief-requests-empty"
        className="rounded-3xl border border-stone-200 bg-white p-6"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-2xl bg-[#00AEEF]/10 text-[#00AEEF] flex items-center justify-center">
            <Inbox size={18}/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#00AEEF]">New Requests Available</div>
            <h2 className="font-serif text-xl mt-0.5 text-[#071A2D] leading-tight">No new briefs match you right now</h2>
          </div>
        </div>
        <p className="mt-3 text-[13.5px] text-stone-600">
          We&apos;ll alert you as soon as a customer broadcasts a request matching your service area.
        </p>
      </section>
    );
  }

  return (
    <section
      data-testid="new-brief-requests"
      className="rounded-3xl border border-[#00AEEF]/25 bg-gradient-to-br from-[#00AEEF]/6 via-white to-white p-5 sm:p-7"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-2xl bg-[#00AEEF]/10 text-[#00AEEF] flex items-center justify-center">
            <Inbox size={18}/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#00AEEF]">New Requests Available</div>
            <h2 className="font-serif text-xl mt-0.5 text-[#071A2D] leading-tight">{visible.length} brief{visible.length === 1 ? "" : "s"} matching your service area</h2>
          </div>
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {visible.map((b) => (
          <article
            key={b.brief_id}
            data-testid={`brief-row-${b.brief_id}`}
            className="rounded-2xl bg-white border border-stone-200 p-4 sm:p-5 hover:border-[#C9A45C]/45 transition"
          >
            <div className="flex items-start gap-3">
              <Sparkles size={16} className="mt-0.5 text-[#C9A45C] shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[#071A2D] leading-snug">{b.request}</div>
                {b.ai_summary && (
                  <p className="mt-1 text-[13px] text-[#071A2D]/70 leading-snug">
                    <span className="text-[#C9A45C] font-semibold mr-1">AI:</span> {b.ai_summary}
                  </p>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-stone-500">
                  <span className="flex items-center gap-1"><MapPin size={11}/> {b.city || "—"}</span>
                  {b.date && <span className="flex items-center gap-1"><Calendar size={11}/> {new Date(b.date).toLocaleDateString(undefined, { month:"short", day:"numeric" })}</span>}
                  <span className="flex items-center gap-1"><Users size={11}/> {b.guests || 2}</span>
                  {b.budget_max != null && (
                    <span className="flex items-center gap-1"><DollarSign size={11}/> up to ${Number(b.budget_max).toLocaleString()}</span>
                  )}
                  <span className="text-[#00AEEF] font-semibold">{b.offer_count} offer{b.offer_count === 1 ? "" : "s"}</span>
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2">
              <button
                type="button"
                data-testid={`brief-send-offer-${b.brief_id}`}
                onClick={() => setOpenModal(b)}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#FF7A45] text-white px-4 py-2 text-[12.5px] font-semibold hover:brightness-105 transition"
              >
                <Send size={13}/> Send Offer
              </button>
              <button
                type="button"
                data-testid={`brief-pass-${b.brief_id}`}
                onClick={() => setHidden((s) => new Set([...s, b.brief_id]))}
                className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-white px-4 py-2 text-[12.5px] font-medium text-stone-700 hover:bg-stone-50"
              >
                Pass
              </button>
            </div>
          </article>
        ))}
      </div>

      {openModal && (
        <SendOfferModal
          brief={openModal}
          onClose={() => setOpenModal(null)}
          onSubmitted={() => {
            // Once an offer ships we don't show the brief in this widget again.
            setHidden((s) => new Set([...s, openModal.brief_id]));
            setOpenModal(null);
            reload();
          }}
        />
      )}
    </section>
  );
}

function SendOfferModal({ brief, onClose, onSubmitted }) {
  const [message, setMessage] = useState("");
  const [estimate, setEstimate] = useState(brief.budget_max ? Math.floor(brief.budget_max * 0.6) : "");
  const [approach, setApproach] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Phase 47: Smart Offer Assistant (TREL+ feature). Locked preview state
  // shows an "Upgrade to TREL+" banner; subscribed providers get one-tap
  // AI-drafted message + estimate + approach.
  const [aiBusy, setAiBusy] = useState(false);
  const [aiErr, setAiErr] = useState("");
  const [aiLocked, setAiLocked] = useState(false);
  const [aiSource, setAiSource] = useState("");

  const aiSuggest = async () => {
    setAiBusy(true);
    setAiErr("");
    setAiLocked(false);
    try {
      const { data } = await api.post(`/briefs/${brief.brief_id}/ai-suggest`);
      const s = data?.suggestion || {};
      if (s.message) setMessage(s.message);
      if (s.starting_estimate) setEstimate(Math.round(Number(s.starting_estimate)));
      if (s.approach) setApproach(s.approach);
      setAiSource(data?.source || "");
    } catch (e) {
      const code = e?.response?.data?.code || e?.response?.data?.detail?.code;
      if (code === "trel_plus_required" || e?.response?.status === 402) {
        setAiLocked(true);
      } else {
        setAiErr(e?.response?.data?.detail || "Could not generate suggestion.");
      }
    } finally {
      setAiBusy(false);
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (message.trim().length < 10) { setErr("Add a personal message (10+ characters)."); return; }
    if (!Number(estimate) || Number(estimate) <= 0) { setErr("Set a starting estimate."); return; }
    setErr("");
    setBusy(true);
    try {
      await api.post(`/briefs/${brief.brief_id}/offers`, {
        message: message.trim(),
        starting_estimate: Number(estimate),
        approach: approach.trim() || null,
      });
      onSubmitted?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not submit offer.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="send-offer-modal"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-[#071A2D]/55 backdrop-blur-sm p-3 sm:p-6"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-lg rounded-3xl bg-white border border-stone-200 shadow-[0_30px_80px_-30px_rgba(7,26,45,0.45)] p-6 sm:p-8"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#00AEEF]">Send your offer</div>
            <h3 className="mt-1 font-serif text-xl text-[#071A2D] leading-tight">{brief.request}</h3>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="w-8 h-8 rounded-full hover:bg-stone-100 text-stone-500 flex items-center justify-center">
            <X size={16}/>
          </button>
        </div>

        {/* Phase 47 — TREL+ Smart Offer Assistant button. One tap pre-fills
            the entire form with an AI-drafted message + estimate + approach.
            Non-TREL+ providers see the locked "Upgrade to TREL+" gate banner
            after their first click. */}
        <div className="mt-4 flex items-start gap-2">
          <button
            type="button"
            data-testid="offer-ai-suggest"
            onClick={aiSuggest}
            disabled={aiBusy}
            className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#C9A45C] to-[#FF7A45] text-white px-4 py-2 text-[12.5px] font-semibold shadow-[0_6px_18px_-6px_rgba(201,164,92,0.55)] hover:brightness-105 disabled:opacity-60"
          >
            <Sparkles size={13}/> {aiBusy ? "Drafting…" : "Smart Suggest (TREL+)"}
          </button>
          {aiSource && !aiLocked && (
            <span className="self-center text-[11px] text-stone-500">
              {aiSource === "emergent_llm" ? "AI-drafted · review before sending" : "Suggested template"}
            </span>
          )}
        </div>

        {aiLocked && (
          <div className="mt-3 rounded-2xl border border-[#C9A45C]/45 bg-gradient-to-r from-[#C9A45C]/8 via-white to-[#FF7A45]/6 p-3 text-[12.5px] text-[#071A2D]">
            <strong className="text-[#C9A45C]">TREL+ feature.</strong> Unlock the Smart Offer Assistant — AI drafts a personalized message + estimate + approach for every brief.{" "}
            <Link to="/provider#ai-assistant" data-testid="offer-trelplus-upgrade" className="font-semibold text-[#FF7A45] underline">Upgrade to TREL+ →</Link>
          </div>
        )}
        {aiErr && (
          <div className="mt-3 text-[12.5px] text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded-lg">{aiErr}</div>
        )}

        <label className="block mt-5">
          <div className="text-[11px] uppercase tracking-wide font-semibold text-stone-500">Personal message *</div>
          <textarea
            data-testid="offer-message"
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Hi [name] — I'd love to handle this for you because…"
            className="mt-1.5 w-full rounded-xl border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00AEEF]/30"
          />
        </label>

        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <label className="block">
            <div className="text-[11px] uppercase tracking-wide font-semibold text-stone-500">Starting estimate ($) *</div>
            <input
              data-testid="offer-estimate"
              type="number"
              min="1"
              value={estimate}
              onChange={(e) => setEstimate(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00AEEF]/30"
            />
          </label>
          <div className="text-[12px] text-stone-500 self-end pb-1">
            Final quote can be customized after they choose you.
          </div>
        </div>

        <label className="block mt-4">
          <div className="text-[11px] uppercase tracking-wide font-semibold text-stone-500">Proposed approach (optional)</div>
          <textarea
            data-testid="offer-approach"
            rows={3}
            value={approach}
            onChange={(e) => setApproach(e.target.value)}
            placeholder="Day 1 lock vendors · Day 2 confirmation call · Day-of on-site coordination…"
            className="mt-1.5 w-full rounded-xl border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00AEEF]/30"
          />
        </label>

        {err && <div className="mt-3 text-[13px] text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded-lg">{err}</div>}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="text-[13px] font-medium text-stone-600 hover:text-stone-900 px-3 py-2">
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="offer-submit"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#FF7A45] text-white px-5 py-2.5 text-[13px] font-semibold hover:brightness-105 disabled:opacity-60"
          >
            {busy ? "Sending…" : (<><Send size={13}/> Send Offer</>)}
          </button>
        </div>
      </form>
    </div>
  );
}
